/**
 * First-release shared-PIN gate (UX only — the cloud API is the authority).
 *
 * RIP OUT: delete this file and unwrap <SiteGate> in main.tsx.
 *
 * The PIN is never in a VITE_* var. Empty SITE_GATE_PIN on the API →
 * GET /api/site-gate returns {enabled: false} and this renders children.
 * Wrong PIN stays on the blank page (no error copy). A 5xx or network
 * failure on the status fetch stays locked (do not fail-open the shop).
 */
import { type FormEvent, type ReactNode, useEffect, useState } from "react";

type GatePhase = "loading" | "open" | "locked";

interface GateStatus {
  enabled: boolean;
  unlocked: boolean;
}

const LOCKED: GateStatus = { enabled: true, unlocked: false };

async function readGateStatus(): Promise<GateStatus> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), 4000);
  try {
    const res = await fetch("/api/site-gate", {
      signal: ctrl.signal,
      credentials: "same-origin",
    });
    if (!res.ok) return LOCKED;
    const data = (await res.json()) as Partial<GateStatus>;
    return {
      enabled: Boolean(data.enabled),
      unlocked: Boolean(data.unlocked),
    };
  } catch {
    // Fail-closed: a 5xx or network error must not render the shop.
    // Gate-off is only {enabled: false} from a successful status response.
    return LOCKED;
  } finally {
    window.clearTimeout(timer);
  }
}

export function SiteGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<GatePhase>("loading");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void readGateStatus().then((status) => {
      if (!active) return;
      setPhase(status.enabled && !status.unlocked ? "locked" : "open");
    });
    return () => {
      active = false;
    };
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!pin || busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/site-gate", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ pin }),
      });
      if (res.ok) {
        setPhase("open");
        return;
      }
      setPin("");
    } catch {
      setPin("");
    } finally {
      setBusy(false);
    }
  }

  if (phase === "loading") {
    return <div data-testid="site-gate-loading" />;
  }
  if (phase === "open") {
    return children;
  }

  // Explicit zinc/white colors — this tree sits outside ThemeProvider, and
  // Tailwind preflight strips native input/button chrome. Brand tokens
  // (bg-bg, border-border, bg-primary) would be unset and stay invisible.
  return (
    <div
      data-testid="site-gate-page"
      className="flex min-h-dvh items-center justify-center bg-zinc-100 px-6 py-10"
    >
      <form
        data-testid="site-gate"
        onSubmit={onSubmit}
        className="flex w-full max-w-sm flex-col gap-3"
      >
        <label
          htmlFor="site-gate-pin"
          className="text-sm font-medium text-zinc-900"
        >
          PIN
        </label>
        <input
          id="site-gate-pin"
          type="password"
          name="pin"
          placeholder="PIN"
          autoComplete="off"
          autoFocus
          aria-label="PIN"
          data-testid="site-gate-pin"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          className="h-12 w-full rounded-md border-2 border-zinc-900 bg-white px-3 text-lg text-zinc-900 outline-none placeholder:text-zinc-400 focus:ring-2 focus:ring-zinc-400"
        />
        <button
          type="submit"
          disabled={busy}
          className="h-12 w-full rounded-md border-2 border-zinc-900 bg-zinc-900 text-base font-medium text-white disabled:opacity-60"
        >
          Enter
        </button>
      </form>
    </div>
  );
}
